import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { EmptyState } from "@/components/feature/WorkStates";
import { specials } from "@/mocks/specials";
import { filterWorks } from "@/utils/work";

export default function Specials() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 400);
    return () => window.clearTimeout(timer);
  }, []);

  const cards = specials.map((special) => ({
    ...special,
    previews: filterWorks({ type: special.type, genre: special.filter, sort: "popularity" }).slice(
      0,
      3,
    ),
  }));

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视专题" }]} />
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            韩国伦理影视专题 - 韩国伦理在线观看
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            我们按照观影主题，把同类型的韩国伦理影视作品整理成专题合集。点击任意专题，即可进入对应的筛选结果页，快速找到你想看的内容。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-8">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="skeleton-shimmer animate-shimmer h-52 w-full" />
                <div className="space-y-3 p-5">
                  <div className="skeleton-shimmer animate-shimmer h-5 w-1/2 rounded" />
                  <div className="skeleton-shimmer animate-shimmer h-4 w-3/4 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <EmptyState
            icon="ri-bookmark-3-line"
            title="暂无专题"
            description="专题内容正在整理中，请稍后再来查看。"
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {cards.map((special) => (
              <div
                key={special.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative h-52 w-full overflow-hidden">
                  <img
                    src={special.cover}
                    alt={`${special.title} 专题封面`}
                    title={`${special.title} 韩国伦理影视专题`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/20 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-background-50">
                    {special.accent}
                  </span>
                  <div className="absolute inset-x-4 bottom-3">
                    <h2 className="font-heading text-lg font-bold text-background-50">
                      {special.title}
                    </h2>
                    <p className="mt-0.5 text-xs text-background-200">{special.subtitle}</p>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <p className="text-sm leading-relaxed text-foreground-500">
                    {special.description}
                  </p>

                  {special.previews.length > 0 && (
                    <div className="mt-4 flex items-center gap-3">
                      {special.previews.map((work) => (
                        <div
                          key={work.id}
                          className="h-20 w-14 shrink-0 overflow-hidden rounded-md border border-background-200"
                        >
                          <img
                            src={work.poster}
                            alt={work.title}
                            title={`${work.title} 韩国伦理影视作品`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                      ))}
                      <span className="text-xs text-foreground-500">
                        等 {special.previews.length}+ 部作品
                      </span>
                    </div>
                  )}

                  <Link
                    to={`/category/${special.type}?filter=${encodeURIComponent(special.filter)}`}
                    className="mt-5 flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-list-2-line text-lg" />
                    进入专题合集
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}